import * as React from 'react';
import { connect } from 'react-redux';
import * as Actions from '../actions';
import { AnalysisSetCard } from './AnalysisSetCard';
import { CoughAnalysisSet, AnalyseState, WaveformType } from '../interfaces/state';
import Alert, { AlertCategoryEnum } from '../../../misc/components/Alert';
import Loading from '../../../misc/components/loading';
import Modal from '../../../misc/components/modal';
import { CoughAnalysisSetsFixedCommentsDisplay, OverreadStatusIdEnum } from '../../../misc/api/apiTypes';
import { useWebPortalApiClient, useWebPortalLegacyClient } from '../../../misc/api/WebPortalTokenClient';
import { useCoughMarshalApiClient } from '../../../misc/api/CoughMarshalApiClient';
import { useAuth } from 'react-oidc-context';
import { useSecurityContext } from '../../../Core/SecurityContext';
import { CoughAnalysisSetTypeEnum } from '../../../misc/api/CoughAnalysisSetTypes';
import { PermissionEnum } from '../../../misc/api/ApiPermissionEnum';

export interface AnalysisSetDisplayDialogProps {
    analysisSetsLoaded: boolean;
    analysisSets: Array<CoughAnalysisSetsFixedCommentsDisplay>;
    isAnalysisSetDialogOpen: boolean;
    isAlertVisible: boolean;
    sessionId: string;
    primaryAnalystSetId: string;
    isViewOnly: boolean;
    waveform: string;
    dispatch: any;
}

export const AnalysisSetDisplayDialog = (props: AnalysisSetDisplayDialogProps) => {
    const legacyClient = useWebPortalLegacyClient();
    const coughMarshalClient = useCoughMarshalApiClient();
    const webPortalClient = useWebPortalApiClient();
    const { currentUserPermissions } = useSecurityContext()
    const { user } = useAuth();
    const [analysisSetSelections, setAnalysisSetSelections] = React.useState<{ [id: string]: boolean }>({});

    React.useEffect(() => {
        const initialSelections: { [id: string]: boolean } = {};
        props.analysisSets.forEach(set => {
            initialSelections[set.Id] = set.IsSelected;
        });
        setAnalysisSetSelections(initialSelections);
    }, [props.analysisSets]);


    const onClose = () => {
        props.dispatch(Actions.CloseAnalysisSetDialogAsyncAction.create(legacyClient, coughMarshalClient, webPortalClient.client, user, currentUserPermissions));
    }

    const renderCards = () => {
        if (props.analysisSetsLoaded) {
            const primaryAnalysisSet = props.analysisSets.find(a => a.Id === props.primaryAnalystSetId);
            const isPrimaryQa = primaryAnalysisSet.Type.Id.includes("QA");
            if (props.analysisSets.length > 0) {
                return props.analysisSets.map(i => {
                    const isPrimaryAnalysisSet = props.primaryAnalystSetId === i.Id;
                    const revealQcPermission = PermissionEnum.RevealQcLayerDuringCoughAnalysisQa;
                    const hasRevealQcPermission = currentUserPermissions.some(p => p.Id === revealQcPermission)
                    const isQc = i.Type.Id == CoughAnalysisSetTypeEnum.QualityCheck;
                    let isQcAndIsQaOpen = isQc;
                    const qaAnalysisSets = props.analysisSets.filter(x => x.Type.Id.includes(CoughAnalysisSetTypeEnum.AuditQualityAssurance) && x.StatusId != OverreadStatusIdEnum.Accepted && x.StatusId != OverreadStatusIdEnum.Rejected)
                    if (isQc) {
                        isQcAndIsQaOpen = qaAnalysisSets.length > 0;
                    }
                    const isBlinded = isPrimaryQa && isQcAndIsQaOpen && !hasRevealQcPermission;

                    const isAbacasDisabled = i.Type.Id == CoughAnalysisSetTypeEnum.AbacasCoughCount && (props.waveform === WaveformType.CWFT_PROCESSED || props.waveform === WaveformType.CWFT_SENSOR_PROCESSED)
                    return (<AnalysisSetCard
                        key={i.Id}
                        id={i.Id}
                        type={i.Type.Name}
                        disabled={isPrimaryAnalysisSet || isBlinded || isAbacasDisabled}
                        isCurrentSet={isPrimaryAnalysisSet}
                        isBlinded={isBlinded}
                        createdDateTime={i.CreatedDateTime}
                        analyst={i.Analyst.Username}
                        acceptability={i.StatusId}
                        checked={analysisSetSelections[i.Id] ?? false}
                        onAnalysisSetSelect={checked => handleAnalysisSetChange(i.Id, checked)} />);
                });
            } else {
                return (
                    <Alert show={true} category={AlertCategoryEnum.Danger}>
                        There are no analysis sets available
                    </Alert>
                );
            }
        } else {
            return <Loading />;
        }
    }

    const renderValidation = () => {
        if (props.isAlertVisible) {
            return (
                <Alert show={true} category={AlertCategoryEnum.Warning}>
                    A maximum of 5 analysis sets can be selected at one time.
                </Alert>
            );
        }
    }

    const handleAnalysisSetChange = (analysisSetId: string, checked: boolean) => {
        setAnalysisSetSelections(prev => ({
            ...prev,
            [analysisSetId]: checked
        }));
    };

    const handleCloseWithoutSave = () => {
        const resetSelections: { [id: string]: boolean } = {};
        props.analysisSets.forEach(set => {
            resetSelections[set.Id] = set.IsSelected;
        });
        setAnalysisSetSelections(resetSelections);
        onClose();
    };

    const handleOkClick = () => {
        Object.entries(analysisSetSelections).forEach(([id, checked]) => {
            props.dispatch(Actions.ToggleAnalysisSetAction.create(id, checked));
        });
        onClose();
    };

    return (
        <Modal show={props.isAnalysisSetDialogOpen} header="Active Analysis Sets" closeModalHandler={handleCloseWithoutSave}>
            <div className="modal-body">
                {renderValidation()}
                <div className="row">
                    <div className="col-md-12">
                        <ul className="posh-select">
                            {renderCards()}
                        </ul>
                    </div>
                </div>
            </div>
            <div className="modal-footer">
                <button className="btn btn-primary" onClick={handleOkClick}>OK</button>
            </div>
        </Modal>
    );
}

function mapStateToProps(state: AnalyseState) {
    return {
        primaryAnalystSetId: state.analysisSet.primaryAnalystSetId,
        analysisSetsLoaded: state.analysisSet.coughAnalysisSets.length > 0,
        analysisSets: state.analysisSet.coughAnalysisSets,
        isAnalysisSetDialogOpen: state.analysisSet.isAnalysisSetDialogOpen,
        isAlertVisible: state.analysisSet.isMaximumAnalysisSetsAlertVisible,
        sessionId: state.core.sessionMetadata.sessionId,
        isViewOnly: state.core.isViewOnly,
        waveform: state.core.selectedWaveform
    } as AnalysisSetDisplayDialogProps;
}

export default connect<AnalysisSetDisplayDialogProps, any>(mapStateToProps)(AnalysisSetDisplayDialog);
